// 列印條碼標籤 — like PetPOS: pick products (from the list selection, search
// or scanning), how many labels each, what to show, then print on an A4
// sticker sheet or a label roll (40×30 mm etc.) through the print dialog
// (AirPrint label printers work). Products without a barcode can get an
// in-store EAN-13 (prefix 20…) generated and saved, so the label scans at 收銀.
import { useEffect, useMemo, useState } from "react";
import { zhEn } from "./posTier.js";
import { Printer, ScanLine, Search, Wand2 } from "lucide-react";
import { C, Btn, Chip, Sheet, Label, Toggle, Stepper, Empty, money, inputStyle } from "./posUi.jsx";
import { effectivePrice, posErrorText } from "./posMath.js";
import { ProductThumb } from "./Register.jsx";
import { savePosProduct } from "../../lib/api.js";

const SHEETS = {
  a4_3x11: { name: zhEn("A4 · 3×11（33 個）", "A4 · 3×11 (33 labels)"), cols: 3, rows: 11, w: 70, h: 25.4 },
  a4_4x8: { name: zhEn("A4 · 4×8（32 個）", "A4 · 4×8 (32 labels)"), cols: 4, rows: 8, w: 52.5, h: 35 },
  a4_5x13: { name: zhEn("A4 · 5×13（65 個）", "A4 · 5×13 (65 labels)"), cols: 5, rows: 13, w: 38.1, h: 21.2 },
  roll_40x30: { name: zhEn("標籤卷 40×30mm", "Label roll 40×30mm"), roll: true, w: 40, h: 30 },
  roll_50x30: { name: zhEn("標籤卷 50×30mm", "Label roll 50×30mm"), roll: true, w: 50, h: 30 },
  roll_60x40: { name: zhEn("標籤卷 60×40mm", "Label roll 60×40mm"), roll: true, w: 60, h: 40 },
};

// EAN-13 check digit.
const ean13 = (twelve) => {
  const d = twelve.split("").map(Number);
  const sum = d.reduce((s, n, i) => s + n * (i % 2 ? 3 : 1), 0);
  return twelve + ((10 - (sum % 10)) % 10);
};
// In-store code: 20 + 10 random digits + check. Retries if already used.
export const makeInStoreBarcode = (used) => {
  for (let i = 0; i < 50; i++) {
    let s = "20";
    for (let k = 0; k < 10; k++) s += Math.floor(Math.random() * 10);
    const code = ean13(s);
    if (!used.has(code)) return code;
  }
  return ean13("20" + String(Date.now()).slice(-10));
};

const esc = (s) => String(s ?? "").replace(/[&<>"]/g, c => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]));

async function barcodeSvg(code) {
  const JsBarcode = (await import("jsbarcode")).default;
  const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
  const format = /^\d{13}$/.test(code) ? "EAN13" : "CODE128";
  try { JsBarcode(svg, code, { format, width: 1.6, height: 40, displayValue: false, margin: 0 }); }
  catch { JsBarcode(svg, code, { format: "CODE128", width: 1.6, height: 40, displayValue: false, margin: 0 }); }
  svg.setAttribute("preserveAspectRatio", "none");
  return svg.outerHTML;
}

export default function BarcodeLabels({ open, onClose, tenant, sym, products, preselect = [], setProducts, onScanOpen }) {
  const [counts, setCounts] = useState({});
  const [q, setQ] = useState("");
  const [size, setSize] = useState(() => { try { return localStorage.getItem("petos:pos:labelSize") || "a4_3x11"; } catch { return "a4_3x11"; } });
  const [show, setShow] = useState({ name: true, price: true, sku: false, shop: false, code: true });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  // Sellable leaf items: variants and plain products (not parents with variants).
  const parentsWithKids = useMemo(() => new Set(products.filter(p => p.parentId).map(p => p.parentId)), [products]);
  const leaves = useMemo(() => products.filter(p => p.status !== "draft" && !parentsWithKids.has(p.id)), [products, parentsWithKids]);
  useEffect(() => {
    if (!open) return;
    const init = {};
    for (const id of preselect) {
      if (parentsWithKids.has(id)) products.filter(v => v.parentId === id && v.status !== "draft").forEach(v => { init[v.id] = Math.max(1, Math.round(v.stock) || 1); });
      else { const p = products.find(x => x.id === id); if (p) init[id] = p.trackStock ? Math.max(1, Math.round(p.stock) || 1) : 1; }
    }
    setCounts(init); setErr("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  useEffect(() => { try { localStorage.setItem("petos:pos:labelSize", size); } catch { /* */ } }, [size]);

  const chosen = leaves.filter(p => counts[p.id] > 0);
  const total = chosen.reduce((s, p) => s + counts[p.id], 0);
  const missing = chosen.filter(p => !p.barcode);
  const found = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return [];
    return leaves.filter(p => p.name.toLowerCase().includes(s) || p.sku.toLowerCase().includes(s) || p.barcode === s).slice(0, 12);
  }, [leaves, q]);
  const bump = (id, n = 1) => setCounts(c => ({ ...c, [id]: Math.max(0, (c[id] || 0) + n) }));

  const genMissing = async () => {
    setBusy(true); setErr("");
    try {
      const used = new Set(products.map(p => p.barcode).filter(Boolean));
      const out = [];
      for (const p of missing) {
        const code = makeInStoreBarcode(used); used.add(code);
        out.push(await savePosProduct({ id: p.id, tenantId: tenant.id, name: p.name, barcode: code }));
      }
      const byId = Object.fromEntries(out.map(r => [r.id, r]));
      setProducts(ps => ps.map(p => byId[p.id] ? { ...p, barcode: byId[p.id].barcode } : p));
    } catch (e) { setErr(posErrorText(e)); } finally { setBusy(false); }
  };

  const print = async () => {
    setBusy(true); setErr("");
    try {
      const cfg = SHEETS[size];
      const cells = [];
      for (const p of chosen) {
        const svg = p.barcode ? await barcodeSvg(p.barcode) : "";
        const price = effectivePrice(p);
        const html = `<div class="lab">
          ${show.shop ? `<div class="shop">${esc(tenant.name)}</div>` : ""}
          ${show.name ? `<div class="nm">${esc(p.name)}</div>` : ""}
          ${show.price ? `<div class="pr">${price < p.price ? `<s>${esc(money(p.price, sym))}</s> ` : ""}${esc(money(price, sym))}</div>` : ""}
          ${svg ? `<div class="bc">${svg}</div>` : `<div class="nobc">（未有條碼）</div>`}
          ${show.code && p.barcode ? `<div class="cd">${esc(p.barcode)}${show.sku && p.sku ? " · " + esc(p.sku) : ""}</div>` : show.sku && p.sku ? `<div class="cd">${esc(p.sku)}</div>` : ""}
        </div>`;
        for (let i = 0; i < counts[p.id]; i++) cells.push(html);
      }
      const page = cfg.roll
        ? `@page { size: ${cfg.w}mm ${cfg.h}mm; margin: 0 } .lab { width: ${cfg.w}mm; height: ${cfg.h}mm; page-break-after: always; }`
        : `@page { size: A4; margin: 10mm 5mm } .grid { display: grid; grid-template-columns: repeat(${cfg.cols}, ${cfg.w}mm); grid-auto-rows: ${cfg.h}mm; justify-content: center }`;
      const doc = `<!doctype html><html><head><meta charset="utf-8"><title>條碼標籤</title><style>
        ${page}
        body { margin: 0; font-family: -apple-system, "PingFang HK", "Noto Sans TC", sans-serif; color: #000 }
        .lab { box-sizing: border-box; padding: 1.2mm 1.6mm; overflow: hidden; display: flex; flex-direction: column; align-items: center; justify-content: center; text-align: center; }
        .shop { font-size: 6.5pt; color: #444 } .nm { font-size: 7.5pt; font-weight: 700; line-height: 1.15; max-height: 2.3em; overflow: hidden }
        .pr { font-size: 10pt; font-weight: 800 } .pr s { font-size: 7pt; font-weight: 400; color: #555 }
        .bc { width: 92%; flex: 1 1 auto; min-height: 5mm; max-height: 12mm } .bc svg { width: 100%; height: 100% }
        .cd { font-size: 6.5pt; letter-spacing: .5px } .nobc { font-size: 7pt; color: #999 }
      </style></head><body>${cfg.roll ? cells.join("") : `<div class="grid">${cells.join("")}</div>`}</body></html>`;
      const iframe = document.createElement("iframe");
      iframe.setAttribute("aria-hidden", "true");
      iframe.style.cssText = "position:fixed;right:0;bottom:0;width:0;height:0;border:0";
      document.body.appendChild(iframe);
      const d = iframe.contentWindow.document;
      d.open(); d.write(doc); d.close();
      setTimeout(() => { try { iframe.contentWindow.focus(); iframe.contentWindow.print(); } catch { /* */ } setTimeout(() => iframe.remove(), 60000); }, 300);
    } catch (e) { setErr(posErrorText(e)); } finally { setBusy(false); }
  };

  if (!open) return null;
  return (
    <Sheet open onClose={onClose} title={zhEn("列印條碼標籤", "Print barcode labels")} maxWidth={640}
      footer={<div>
        {err && <p style={{ color: C.red, fontWeight: 800, fontSize: 13, textAlign: "center", margin: "0 0 8px" }}>{err}</p>}
        <Btn style={{ width: "100%" }} disabled={!total || busy} onClick={print}><Printer size={18} /> {busy ? zhEn("準備中…", "Preparing…") : zhEn("列印 ", "Print ") + total + zhEn(" 個標籤", " labels")}</Btn>
      </div>}>
      <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
        <div style={{ flex: 1, position: "relative" }}>
          <Search size={18} color={C.faint} style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)" }} />
          <input value={q} onChange={e => setQ(e.target.value)} placeholder={zhEn("搜尋加入", "Search to add")} style={{ ...inputStyle, paddingLeft: 38 }} />
        </div>
        <Btn kind="soft" onClick={() => onScanOpen((code) => { const p = leaves.find(x => x.barcode === code || x.sku === code); if (p) { bump(p.id); return true; } return false; }, zhEn("掃描加入標籤（每掃一次 +1）", "Scan to add labels (+1 per scan)"))} style={{ padding: "0 14px" }}><ScanLine size={18} /></Btn>
      </div>
      {found.map(p => (
        <button key={p.id} type="button" onClick={() => { bump(p.id); setQ(""); }} style={{ width: "100%", display: "flex", alignItems: "center", gap: 10, padding: 8, border: "none", borderBottom: "1px solid " + C.line, background: "#fff", fontFamily: "inherit", cursor: "pointer", textAlign: "left" }}>
          <ProductThumb p={p} size={34} radius={10} /><span style={{ flex: 1, fontWeight: 800, color: C.ink }}>{p.name}</span><span style={{ fontWeight: 900 }}>＋</span>
        </button>
      ))}

      <Label style={{ marginTop: 12 }}>{zhEn("標籤（", "Labels (")}{chosen.length} {zhEn("款 ·", "products ·")} {total} {zhEn("個）", "labels)")}</Label>
      {chosen.length === 0 ? <Empty icon="🏷️" title={zhEn("未揀商品", "No products chosen")} sub={zhEn("喺商品列表撳「批量」揀，或者搜尋 / 掃描加入", "Tap \"Bulk\" in the product list to choose, or search / scan to add")} />
        : chosen.map(p => (
          <div key={p.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 0", borderBottom: "1px solid " + C.line }}>
            <ProductThumb p={p} size={36} radius={10} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <p style={{ margin: 0, fontWeight: 900, color: C.ink, fontSize: 14, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.name}</p>
              <p style={{ margin: 0, fontWeight: 700, fontSize: 12, color: p.barcode ? C.sub : C.amber }}>{p.barcode || zhEn("⚠️ 未有條碼", "⚠️ No barcode")} · {money(effectivePrice(p), sym)}</p>
            </div>
            <Stepper value={counts[p.id]} min={0} onChange={v => setCounts(c => ({ ...c, [p.id]: v }))} />
          </div>
        ))}
      {missing.length > 0 && (
        <div style={{ background: C.amberSoft, borderRadius: 14, padding: 12, marginTop: 10 }}>
          <p style={{ margin: "0 0 8px", fontSize: 13, fontWeight: 800, color: "#92400E" }}>{missing.length} {zhEn("件商品未有條碼。可以自動產生店內條碼（20 開頭 EAN-13）並儲存，貼咗標籤就可以喺收銀掃。", "products have no barcode. You can generate in-store barcodes (EAN-13 starting with 20) and save them — stick the label on and you can scan it at the till.")}</p>
          <Btn small kind="ghost" disabled={busy} onClick={genMissing}><Wand2 size={15} /> {zhEn("自動產生條碼", "Generate barcodes automatically")}</Btn>
        </div>
      )}

      <Label style={{ marginTop: 14 }}>{zhEn("紙張", "Paper")}</Label>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 12 }}>
        {Object.entries(SHEETS).map(([k, v]) => <Chip key={k} on={size === k} onClick={() => setSize(k)}>{v.name}</Chip>)}
      </div>
      <Label>{zhEn("顯示", "Show")}</Label>
      {[["name", zhEn("商品名", "Product name")], ["price", zhEn("售價（特價會顯示原價）", "Price (shows the list price when on sale)")], ["code", zhEn("條碼數字", "Barcode digits")], ["sku", "SKU"], ["shop", zhEn("店名", "Shop name")]].map(([k, l]) => (
        <div key={k} style={{ display: "flex", alignItems: "center", gap: 12, padding: "8px 2px", borderBottom: "1px solid " + C.line }}>
          <span style={{ flex: 1, fontWeight: 800, color: C.ink, fontSize: 14 }}>{l}</span>
          <Toggle on={!!show[k]} onChange={v => setShow(s => ({ ...s, [k]: v }))} />
        </div>
      ))}
    </Sheet>
  );
}

